import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { RightChevron } from "@/components/icons";
import { requestAndApprovalApi } from "@/features/request-and-approval/services/request-and-approval.api";
import { supportTicketsApi } from "@/features/support-tickets/services/support-tickets.api";
import { dashboardApi } from "../../services/dashboard.api";
import ProvinceLearningMap from "../ProvinceLearningMap";
import styles from "./TodaysTasksWidget.module.scss";

interface TaskCardData {
  key: string;
  label: string;
  count: number | null;
  hint: string;
  target: string;
  accent: "red" | "amber" | "blue";
}

interface TodaysTasksWidgetProps {
  showProvinceLearningMap: boolean;
  onProvinceLearningMapChange: (checked: boolean) => void;
}

/**
 * Dashboard'un en üstünde "bugün ne yapmalıyım" özeti: onay bekleyen talep,
 * 24 saatteki eğitim ve açık destek talebi sayıları. Her kart tıklanınca
 * ilgili sayfaya yönlendirir. Kullanıcının yetkisi yoksa o kart gizlenir.
 */
const TodaysTasksWidget: React.FC<TodaysTasksWidgetProps> = ({
  showProvinceLearningMap,
  onProvinceLearningMapChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const canSeeApprovals = hasPermission(PERMISSIONS.REQUEST_AND_APPROVAL_VIEW);
  const canSeeEducations = hasPermission(PERMISSIONS.EDUCATION_VIEW);
  const canSeeSupport = hasPermission(PERMISSIONS.SUPPORT_TICKETS_VIEW);
  const canSeeProvinceLearningMap = hasPermission(
    PERMISSIONS.DASHBOARD_EDUCATION_MAP_VIEW,
  );

  const [pendingApprovalCount, setPendingApprovalCount] = useState<
    number | null
  >(null);
  const [upcoming24hCount, setUpcoming24hCount] = useState<number | null>(null);
  const [openSupportCount, setOpenSupportCount] = useState<number | null>(null);

  useEffect(() => {
    if (!canSeeApprovals) {
      setPendingApprovalCount(null);
      return;
    }
    requestAndApprovalApi
      .getAll({
        page: 1,
        pageSize: 1,
        filters: { status: "manage_requests" },
      })
      .then((res) => setPendingApprovalCount(res.total ?? 0))
      .catch(() => setPendingApprovalCount(0));
  }, [canSeeApprovals]);

  useEffect(() => {
    if (!canSeeEducations) {
      setUpcoming24hCount(null);
      return;
    }
    // Basit yaklaşım: dashboard'un mevcut "yaklaşan eğitimler" listesinden
    // 24 saat içindekileri sayıyoruz (mock veriyle uyumlu).
    dashboardApi
      .getUpcomingTrainings({ page: 1, pageSize: 50 })
      .then((res) => {
        const now = Date.now();
        const in24h = now + 24 * 60 * 60 * 1000;
        const count = (res.data ?? []).filter((row: any) => {
          const raw = row.startDate ?? row.publicationDate;
          if (!raw) return false;
          const ts = new Date(raw).getTime();
          return Number.isFinite(ts) && ts >= now && ts <= in24h;
        }).length;
        setUpcoming24hCount(count);
      })
      .catch(() => setUpcoming24hCount(0));
  }, [canSeeEducations]);

  useEffect(() => {
    if (!canSeeSupport) {
      setOpenSupportCount(null);
      return;
    }
    supportTicketsApi
      .getStats()
      .then((s) => setOpenSupportCount(s.pendingCount ?? 0))
      .catch(() => setOpenSupportCount(0));
  }, [canSeeSupport]);

  const cards: TaskCardData[] = useMemo(() => {
    const list: TaskCardData[] = [];
    if (canSeeApprovals) {
      list.push({
        key: "pendingApprovals",
        label: t("dashboard.todaysTasks.pendingApprovals"),
        count: pendingApprovalCount,
        hint: t("dashboard.todaysTasks.pendingApprovalsHint"),
        target: "/dashboard/request-and-approval",
        accent: "red",
      });
    }
    if (canSeeEducations) {
      list.push({
        key: "upcoming24h",
        label: t("dashboard.todaysTasks.upcoming24h"),
        count: upcoming24hCount,
        hint: t("dashboard.todaysTasks.upcoming24hHint"),
        target: "/dashboard/education-management?status=2",
        accent: "blue",
      });
    }
    if (canSeeSupport) {
      list.push({
        key: "openSupport",
        label: t("dashboard.todaysTasks.openSupport"),
        count: openSupportCount,
        hint: t("dashboard.todaysTasks.openSupportHint"),
        target: "/dashboard/support-tickets",
        accent: "amber",
      });
    }
    return list;
  }, [
    t,
    canSeeApprovals,
    canSeeEducations,
    canSeeSupport,
    pendingApprovalCount,
    upcoming24hCount,
    openSupportCount,
  ]);

  if (cards.length === 0 && !canSeeProvinceLearningMap) return null;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {showProvinceLearningMap
            ? "Eğitim Haritası"
            : t("dashboard.todaysTasks.title")}
        </h3>
        {canSeeProvinceLearningMap && (
          <div className={styles.mapToggle}>
            <span>Bölgesel görünüm</span>
            <button
              type="button"
              className={styles.mapSwitch}
              data-checked={showProvinceLearningMap ? "true" : "false"}
              role="switch"
              aria-checked={showProvinceLearningMap}
              aria-label="Bölgesel görünüm"
              onClick={() =>
                onProvinceLearningMapChange(!showProvinceLearningMap)
              }
            >
              <span />
            </button>
          </div>
        )}
      </div>
      {showProvinceLearningMap && canSeeProvinceLearningMap ? (
        <ProvinceLearningMap />
      ) : cards.length > 0 ? (
        <div className={styles.grid}>
          {cards.map((card) => (
            <button
              key={card.key}
              type="button"
              className={`${styles.card} ${styles[`accent-${card.accent}`]}`}
              onClick={() => navigate(card.target)}
            >
              <div className={styles.cardTop}>
                <span className={styles.cardLabel}>{card.label}</span>
                <RightChevron
                  width={18}
                  height={18}
                  className={styles.cardArrow}
                />
              </div>
              <div className={styles.cardValue}>
                {card.count == null ? "—" : card.count.toLocaleString("tr-TR")}
              </div>
              <div className={styles.cardHint}>{card.hint}</div>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default TodaysTasksWidget;
